var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { z } from "zod";
import moment from "moment";
import { DISPLAY_DATE_FORMAT, toApiDate } from "./dateUtils";
import { isValidPriceInput, priceInputToMinorUnits } from "./priceUtils";
import { validateEAN13 } from "./ean13Utils";
import { MAIN_NAME_TYPE_VALUE } from "./titleDisplay";
import { violatesThemaQualifierRule } from "./themaSubjectRules";
import { schemesWithForbiddenMainSubject, schemesWithMultipleMainSubjects, } from "./mainSubjectRules";
import { hasImageLinkExtension, hasPdfLinkExtension, isImageResourceType, isTableOfContentsType, requiresImageMode, } from "./resourceImageRules";
import { PublisherRegistrationAgencyEnum, CollectionTypeEnum, CollectionMediumEnum, CollectionStatusEnum, PersonIdentifierTypeEnum, PersonLegalFormEnum, PersonStatusEnum, ProductAccessibilityEnum, PublisherPrefixRangeStatusEnum, ProductNumberBlockReasonEnum, FormatEnum, IpaReportParamsRegistrationAgencyEnum, PiidExportParamsRegistrationAgencyEnum, ProgressReportParamsRegistrationAgencyEnum, WipoReportParamsRegistrationAgencyEnum, } from "../types/Api";
export var HTTP_URL_REGEX = /^https?:\/\/[^\s$.?#].[^\s]*$/i;
export var createInternationalPrefixSchema = function (formatMessage) {
    return z.object({
        id: z.string().optional(),
        agency: z.nativeEnum(PublisherRegistrationAgencyEnum, {
            errorMap: function () { return ({
                message: formatMessage({
                    id: "ui-br-agency.international-prefix.validation.agency",
                }),
            }); },
        }),
        value: z.string().nonempty({
            message: formatMessage({
                id: "ui-br-agency.international-prefix.validation.value",
            }),
        }),
    });
};
export var createAgencyPrefixSchema = function (formatMessage) {
    return z.object({
        id: z.string().optional(),
        internationalPrefix: createInternationalPrefixSchema(formatMessage).required(),
        value: z.string().nonempty({
            message: formatMessage({
                id: "ui-br-agency.agency-prefix.validation.value",
            }),
        }),
    });
};
export var createCollectionSchema = function (formatMessage) {
    return z
        .object({
        type: z
            .union([
            z.literal(""),
            z.literal(undefined),
            z.nativeEnum(CollectionTypeEnum),
        ])
            .refine(function (val) { return val !== "" && val !== undefined; }, {
            message: formatMessage({
                id: "ui-br-agency.collections.validation.type",
            }),
        }),
        publisher: z
            .object({
            id: z.string(),
            name: z.string(),
        })
            .passthrough()
            .optional(),
        issn: z.string(),
        mainName: z.string().nonempty({
            message: formatMessage({
                id: "ui-br-agency.collections.validation.mainName",
            }),
        }),
        // DB-787: ISSN register attributes - kept in the payload so an edit does not wipe them
        qualifier: z
            .string()
            .nullish()
            .transform(function (val) { return ((val === null || val === void 0 ? void 0 : val.trim()) ? val.trim() : undefined); }),
        status: z
            .union([z.literal(""), z.nativeEnum(CollectionStatusEnum)])
            .nullish()
            .transform(function (val) { return val || undefined; }),
        endYear: z.preprocess(function (val) {
            if (val === null || val === undefined)
                return undefined;
            var text = String(val).trim();
            if (text === "")
                return undefined;
            return /^\d{4}$/.test(text) ? Number(text) : text;
        }, z
            .number({
            invalid_type_error: formatMessage({
                id: "ui-br-agency.collections.validation.endYear",
            }),
        })
            .int()
            .optional()),
        medium: z
            .union([z.literal(""), z.nativeEnum(CollectionMediumEnum)])
            .nullish()
            .transform(function (val) { return val || undefined; }),
        otherLanguageNames: z.array(z.object({
            id: z.string().optional(),
            name: z.string().nonempty({
                message: formatMessage({
                    id: "ui-br-agency.collections.validation.otherLanguageName",
                }),
            }),
        })),
    })
        .refine(function (data) {
        return data.type !== CollectionTypeEnum.PUBLISHER_SERIES ||
            Boolean(data.publisher);
    }, {
        message: formatMessage({
            id: "ui-br-agency.collections.validation.publisherForPublisherSeries",
        }),
        path: ["publisher"],
    });
};
var YEAR_RE = /^\d{4}$/;
var MONTH_YEAR_RE = /^(0[1-9]|1[0-2])\.\d{4}$/;
var FULL_DATE_RE = /^(0[1-9]|[12]\d|3[01])\.(0[1-9]|1[0-2])\.\d{4}$/;
var isValidPartialDate = function (val) {
    if (YEAR_RE.test(val))
        return true;
    if (MONTH_YEAR_RE.test(val))
        return true;
    if (FULL_DATE_RE.test(val)) {
        var _a = val.split(".").map(Number), day = _a[0], month = _a[1], year = _a[2];
        var date = new Date(year, month - 1, day);
        return (date.getFullYear() === year &&
            date.getMonth() === month - 1 &&
            date.getDate() === day);
    }
    return false;
};
var parsePartialDateYear = function (val) {
    if (!val)
        return null;
    var parts = val.split(".");
    return Number(parts[parts.length - 1]);
};
var parsePartialDateParts = function (val) {
    if (!val)
        return null;
    var parts = val.split(".");
    if (parts.length === 1)
        return { year: Number(parts[0]), month: null, day: null };
    if (parts.length === 2)
        return { year: Number(parts[1]), month: Number(parts[0]), day: null };
    return {
        year: Number(parts[2]),
        month: Number(parts[1]),
        day: Number(parts[0]),
    };
};
var partialDateSchema = function (formatMessage) {
    return z
        .union([
        z.literal(""),
        z.string().refine(function (val) { return isValidPartialDate(val); }, {
            message: formatMessage({
                id: "ui-br-agency.validation.partial-date.format",
            }),
        }),
    ])
        .optional();
};
var naturalPersonObjectSchema = function (formatMessage) {
    return z.object({
        id: z.string().optional(),
        sourceId: z.string().optional(),
        legalForm: z
            .nativeEnum(PersonLegalFormEnum, {
            errorMap: function () { return ({
                message: formatMessage({
                    id: "ui-br-agency.natural-person.validation.legalForm",
                }),
            }); },
        })
            .optional(),
        status: z
            .nativeEnum(PersonStatusEnum, {
            errorMap: function () { return ({
                message: formatMessage({
                    id: "ui-br-agency.natural-person.validation.status",
                }),
            }); },
        })
            .optional(),
        firstName: z.string().nonempty({
            message: formatMessage({
                id: "ui-br-agency.natural-person.validation.firstName",
            }),
        }),
        lastName: z.string().nonempty({
            message: formatMessage({
                id: "ui-br-agency.natural-person.validation.lastName",
            }),
        }),
        birthDate: partialDateSchema(formatMessage).refine(function (dateValue) {
            if (!dateValue)
                return true;
            var year = parsePartialDateYear(dateValue);
            return year !== null && year <= new Date().getFullYear();
        }, {
            message: formatMessage({
                id: "ui-br-agency.natural-person.validation.birthDate.max",
            }),
        }),
        deathDate: partialDateSchema(formatMessage).refine(function (dateValue) {
            if (!dateValue)
                return true;
            var year = parsePartialDateYear(dateValue);
            return year !== null && year <= new Date().getFullYear();
        }, {
            message: formatMessage({
                id: "ui-br-agency.natural-person.validation.deathDate.max",
            }),
        }),
        // Required unless the person is a local-only authority: Aleph (NK
        // authorities) rejects an author record without field 678, which is built
        // from this note (DB-416). The check lives in refineNaturalPersonNote, as it
        // depends on localOnly (DB-635).
        biographicalNote: z
            .string({
            invalid_type_error: formatMessage({
                id: "ui-br-agency.natural-person.validation.biographicalNote",
            }),
        })
            .nullish(),
        disclosureConsent: z.boolean().optional(),
        // Local-only authority: kept in ReČeK, not sent to NK authorities (DB-635).
        localOnly: z.boolean().optional(),
        naturalAlternativeNames: z
            .array(z.object({
            id: z.string().optional(),
            firstName: z.string().optional(),
            lastName: z.string().optional(),
        }))
            .optional(),
        identifiers: z
            .array(z.object({
            id: z.string().optional(),
            type: z.nativeEnum(PersonIdentifierTypeEnum, {
                errorMap: function () { return ({
                    message: formatMessage({
                        id: "ui-br-agency.natural-person.validation.identifierType",
                    }),
                }); },
            }),
            identifier: z.string().nonempty({
                message: formatMessage({
                    id: "ui-br-agency.natural-person.validation.identifier",
                }),
            }),
        }))
            .optional(),
    });
};
/**
 * Requires the biographical note of a natural person unless it is a local-only
 * authority, which is never sent to NK authorities (DB-635).
 */
var refineNaturalPersonNote = function (formatMessage, data, ctx) {
    var _a;
    if (data.localOnly === true)
        return;
    if (((_a = data.biographicalNote) !== null && _a !== void 0 ? _a : "").trim().length > 0)
        return;
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["biographicalNote"],
        message: formatMessage({
            id: "ui-br-agency.natural-person.validation.biographicalNote",
        }),
    });
};
export var createInlineNaturalPersonSchema = function (formatMessage) {
    return naturalPersonObjectSchema(formatMessage)
        .pick({
        firstName: true,
        lastName: true,
        birthDate: true,
        deathDate: true,
        biographicalNote: true,
        disclosureConsent: true,
        localOnly: true,
        identifiers: true,
        naturalAlternativeNames: true,
    })
        .superRefine(function (data, ctx) {
        refineNaturalPersonNote(formatMessage, data, ctx);
        if (!data.deathDate && data.disclosureConsent !== true) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["disclosureConsent"],
                message: formatMessage({
                    id: "ui-br-agency.natural-person.disclosureConsent.field.required",
                }),
            });
        }
    });
};
export var createNaturalPersonSchema = function (formatMessage) {
    return naturalPersonObjectSchema(formatMessage).superRefine(function (data, ctx) {
        refineNaturalPersonNote(formatMessage, data, ctx);
        if (!data.deathDate && data.disclosureConsent !== true) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["disclosureConsent"],
                message: formatMessage({
                    id: "ui-br-agency.natural-person.disclosureConsent.field.required",
                }),
            });
        }
        if (data.birthDate && data.deathDate) {
            var birthParts = parsePartialDateParts(data.birthDate);
            var deathParts = parsePartialDateParts(data.deathDate);
            if (birthParts && deathParts) {
                if (deathParts.year < birthParts.year) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ["deathDate"],
                        message: formatMessage({
                            id: "ui-br-agency.natural-person.validation.deathDate.afterBirth",
                        }),
                    });
                    return;
                }
                if (deathParts.year === birthParts.year &&
                    deathParts.month !== null &&
                    birthParts.month !== null) {
                    if (deathParts.month < birthParts.month) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ["deathDate"],
                            message: formatMessage({
                                id: "ui-br-agency.natural-person.validation.deathDate.afterBirth",
                            }),
                        });
                        return;
                    }
                    if (deathParts.month === birthParts.month &&
                        deathParts.day !== null &&
                        birthParts.day !== null &&
                        deathParts.day <= birthParts.day) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ["deathDate"],
                            message: formatMessage({
                                id: "ui-br-agency.natural-person.validation.deathDate.afterBirth",
                            }),
                        });
                    }
                }
            }
        }
    });
};
export var createPublisherPrefixRangeSchema = function (formatMessage) {
    return z.object({
        id: z.string().optional(),
        agencyPrefix: createAgencyPrefixSchema(formatMessage),
        magnitude: z.preprocess(function (val) {
            if (typeof val === "string") {
                var trimmed = val.trim();
                if (trimmed === "")
                    return undefined;
                var num = Number(trimmed);
                return isNaN(num) ? val : num;
            }
            return val;
        }, z.number({
            required_error: formatMessage({
                id: "ui-br-agency.publisher-prefix-range.validation.magnitude",
            }),
        })),
        start: z.string().nonempty({
            message: formatMessage({
                id: "ui-br-agency.publisher-prefix-range.validation.start",
            }),
        }),
        end: z.string().nonempty({
            message: formatMessage({
                id: "ui-br-agency.publisher-prefix-range.validation.end",
            }),
        }),
        status: z.string().nonempty({
            message: formatMessage({
                id: "ui-br-agency.publisher-prefix-range.validation.status",
            }),
        }),
        nextSequence: z.string().optional(),
        version: z.number().optional(),
    });
};
export var createTitleSchema = function (formatMessage) {
    var requiredOnix = function (msgId) {
        return z.preprocess(function (v) { return v !== null && v !== void 0 ? v : {}; }, z
            .object({
            id: z.string().optional(),
            value: z.string().optional(),
        })
            .passthrough()
            .superRefine(function (v, ctx) {
            var hasId = typeof (v === null || v === void 0 ? void 0 : v.id) === "string" && v.id.trim() !== "";
            var hasValue = typeof (v === null || v === void 0 ? void 0 : v.value) === "string" && v.value.trim() !== "";
            if (!hasId && !hasValue) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["id"],
                    message: formatMessage({ id: msgId }),
                });
            }
        }));
    };
    var requiredEntityWithId = function (msgId) {
        return z.preprocess(function (v) { return v !== null && v !== void 0 ? v : {}; }, z
            .object({
            id: z
                .string({
                required_error: formatMessage({ id: msgId }),
                invalid_type_error: formatMessage({ id: msgId }),
            })
                .min(1, { message: formatMessage({ id: msgId }) }),
        })
            .passthrough());
    };
    var titleContributionItemSchema = z
        .object({
        sequenceNumber: z.number().int().positive().optional(),
        person: z
            .object({ id: z.string().min(1).optional() })
            .passthrough()
            .optional(),
        firstName: z.string().optional(),
        lastName: z.string().optional(),
        biographicalNote: z.string().optional(),
        name: z.string().optional(),
        roles: z
            .array(z.object({
            role: requiredOnix("ui-br-agency.title.validation.contributionRole"),
        }))
            .optional(),
        unnamedPerson: requiredOnix("ui-br-agency.title.validation.unnamedPerson").optional(),
    })
        .passthrough();
    var httpUrlRegex = HTTP_URL_REGEX;
    var DOI_URL_REGEX = /^https?:\/\/(?:dx\.)?doi\.org\/10\.\d{4,9}\/[-._;()/:A-Z0-9]+$/i;
    var requiredArray = function (schema, msgId) {
        return z.array(schema).min(1, { message: formatMessage({ id: msgId }) });
    };
    var nameItemSchema = z.object({
        id: z.string().optional(),
        type: requiredOnix("ui-br-agency.title.validation.nameType"),
        name: z.string().min(1, {
            message: formatMessage({ id: "ui-br-agency.title.validation.name" }),
        }),
        subtitle: z.string().optional(),
        prefix: z.string().optional(),
        titleWithoutPrefix: z.string().optional(),
        partNumber: z.string().optional(),
        partName: z.string().optional(),
    });
    var languageItemSchema = z.object({
        id: z.string().optional(),
        role: requiredOnix("ui-br-agency.title.validation.languageRole"),
        language: requiredOnix("ui-br-agency.title.validation.language"),
    });
    var subjectItemSchema = z.object({
        id: z.string().optional(),
        mainSubject: z.boolean().optional(),
        scheme: requiredOnix("ui-br-agency.title.validation.subjectScheme"),
        code: z.string().optional(),
        text: z.string().optional(),
    });
    var textContentItemSchema = z.object({
        id: z.string().optional(),
        type: requiredOnix("ui-br-agency.title.validation.contentType"),
        text: z.string().min(1, {
            message: formatMessage({
                id: "ui-br-agency.title.validation.contentText",
            }),
        }),
        language: z.any().optional(),
    });
    var supportingResourceItemSchema = z
        .object({
        id: z.string().optional(),
        type: requiredOnix("ui-br-agency.title.validation.resourceType"),
        mode: requiredOnix("ui-br-agency.title.validation.resourceMode"),
        link: z
            .string()
            .trim()
            .min(1, {
            message: formatMessage({
                id: "ui-br-agency.title.validation.resourceLink",
            }),
        })
            .refine(function (val) { return httpUrlRegex.test(val); }, {
            message: formatMessage({
                id: "ui-br-agency.title.validation.resourceLink.web",
            }),
        }),
    })
        .superRefine(function (resource, ctx) {
        // Table of contents (ONIX list 158 code 25) links a PDF — issue #75957.
        if (isTableOfContentsType(resource.type) &&
            !hasPdfLinkExtension(resource.link)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["link"],
                message: formatMessage({
                    id: "ui-br-agency.title.validation.resourceLink.pdf",
                }),
            });
        }
        // Image content types (ONIX list 158 codes 01–10) — issue #75515.
        if (!isImageResourceType(resource.type))
            return;
        if (requiresImageMode(resource.type, resource.mode)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["mode"],
                message: formatMessage({
                    id: "ui-br-agency.title.validation.resourceMode.image",
                }),
            });
        }
        if (!hasImageLinkExtension(resource.link)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["link"],
                message: formatMessage({
                    id: "ui-br-agency.title.validation.resourceLink.image",
                }),
            });
        }
    });
    var publicationPlaceItemSchema = z.object({
        id: z.string().optional(),
        city: z.string().min(1, {
            message: formatMessage({
                id: "ui-br-agency.title.validation.publicationCity",
            }),
        }),
    });
    var audienceItemSchema = z.object({
        id: z.string().optional(),
        type: requiredOnix("ui-br-agency.title.validation.audienceType"),
    });
    var titlePublisherItemSchema = z.object({
        id: z.string().optional(),
        publisher: requiredEntityWithId("ui-br-agency.title.validation.publisher"),
        role: requiredOnix("ui-br-agency.title.validation.publisherRole"),
    });
    var titleCollectionItemSchema = z.object({
        id: z.string().optional(),
        collection: requiredEntityWithId("ui-br-agency.title.validation.collection"),
        partNumber: z.string().optional(),
    });
    var invalidNumberMessage = function (labelId) {
        return formatMessage({ id: "ui-br-agency.validation.number.invalid" }, { field: formatMessage({ id: labelId }) });
    };
    var optionalInt = function (labelId) {
        return z.coerce
            .number({ invalid_type_error: invalidNumberMessage(labelId) })
            .int({ message: invalidNumberMessage(labelId) });
    };
    var coerceOptionalIntSchema = function (schema) {
        return z
            .preprocess(function (v) { return (v === "" || v == null ? undefined : v); }, schema)
            .optional();
    };
    var coerceOptionalInt = function (labelId, min, max, msgId) {
        return coerceOptionalIntSchema(optionalInt(labelId)
            .min(min, { message: formatMessage({ id: msgId }) })
            .max(max, { message: formatMessage({ id: msgId }) }));
    };
    var schema = z
        .object({
        owner: requiredEntityWithId("ui-br-agency.title.validation.owner"),
        // A title record must contain exactly one main title name (ONIX
        // Codetable 15 code "01"); other name types may repeat freely. The count
        // rule is attached to the names array itself (not the object-level
        // superRefine) so it validates independently of other fields — an
        // object-level superRefine is skipped by zod when any sibling field fails,
        // which would mask this error on a new title with other empty fields.
        names: requiredArray(nameItemSchema, "ui-br-agency.title.validation.names.required").superRefine(function (names, ctx) {
            var mainNameCount = (names !== null && names !== void 0 ? names : []).filter(function (name) { var _a; return ((_a = name === null || name === void 0 ? void 0 : name.type) === null || _a === void 0 ? void 0 : _a.value) === MAIN_NAME_TYPE_VALUE; }).length;
            if (mainNameCount !== 1) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [],
                    message: formatMessage({
                        id: "ui-br-agency.title.validation.mainName.exactlyOne",
                    }),
                });
            }
        }),
        languages: requiredArray(languageItemSchema, "ui-br-agency.title.validation.languages.required"),
        // A Thema qualifier (scheme 94–99) requires a Thema subject category
        // term (scheme 93) on the title — issue #75750. Attached to the array
        // (path []) so the Témata section banner shows it independently of
        // other fields, like the names rule above.
        subjects: z.array(subjectItemSchema).superRefine(function (subjects, ctx) {
            if (violatesThemaQualifierRule(subjects)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [],
                    message: formatMessage({
                        id: "ui-br-agency.title.validation.subjects.themaQualifierWithoutCategory",
                    }),
                });
            }
            // Main subject per scheme — issue #76113. A scheme without a main
            // subject is valid; the subjects section only recommends one.
            if (schemesWithMultipleMainSubjects(subjects).length > 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [],
                    message: formatMessage({
                        id: "ui-br-agency.title.validation.subjects.multipleMainInScheme",
                    }),
                });
            }
            if (schemesWithForbiddenMainSubject(subjects).length > 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [],
                    message: formatMessage({
                        id: "ui-br-agency.title.validation.subjects.mainNotAllowed",
                    }),
                });
            }
        }),
        textContents: z.array(textContentItemSchema),
        supportingResources: z.array(supportingResourceItemSchema),
        publicationPlaces: requiredArray(publicationPlaceItemSchema, "ui-br-agency.title.validation.places.required"),
        audiences: z.array(audienceItemSchema),
        titlePublishers: requiredArray(titlePublisherItemSchema, "ui-br-agency.title.validation.titlePublishers.required"),
        titleContributions: z.array(titleContributionItemSchema).optional(),
        titleCollections: z.array(titleCollectionItemSchema).optional(),
        editionNumber: coerceOptionalIntSchema(optionalInt("ui-br-agency.title.editionNumber.field.label").min(1, {
            message: formatMessage({
                id: "ui-br-agency.title.validation.editionNumber.min",
            }),
        })),
        editionStatement: z.any().optional(),
        ageFrom: coerceOptionalInt("ui-br-agency.title.ageFrom.field.label", 0, 99, "ui-br-agency.title.validation.age.range"),
        ageTo: coerceOptionalInt("ui-br-agency.title.ageTo.field.label", 0, 99, "ui-br-agency.title.validation.age.range"),
        doi: z
            .preprocess(function (v) { return (typeof v === "string" ? v.trim() : v); }, z.string().optional())
            .refine(function (val) { return !val || DOI_URL_REGEX.test(val); }, {
            message: formatMessage({
                id: "ui-br-agency.title.validation.doi.format",
            }),
        }),
        illustrationsNote: z.any().optional(),
        imprint: z.any().optional(),
        commentForAgency: z.any().optional(),
        contributions: z.any().optional(),
    })
        .superRefine(function (data, ctx) {
        if (data.ageFrom != null &&
            data.ageTo != null &&
            data.ageFrom > data.ageTo) {
            var msg = formatMessage({
                id: "ui-br-agency.title.validation.age.order",
            });
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["ageFrom"],
                message: msg,
            });
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["ageTo"],
                message: msg,
            });
        }
    });
    return schema;
};
export var createPublisherPrefixSchema = function (formatMessage) {
    var requiredMessage = formatMessage({
        id: "ui-br-agency.validation.required",
    });
    return z.object({
        id: z.any().optional(),
        publisherPrefixRange: z.any().optional(),
        publisher: z.preprocess(function (value) { return value !== null && value !== void 0 ? value : {}; }, z
            .object({
            id: z
                .string({ required_error: requiredMessage })
                .min(1, requiredMessage),
        })
            .passthrough()),
        value: z.any().optional(),
        status: z.nativeEnum(PublisherPrefixRangeStatusEnum, {
            errorMap: function () { return ({
                message: formatMessage({
                    id: "ui-br-agency.publisher-prefix.validation.status",
                }),
            }); },
        }),
        nextSequence: z.any().optional(),
    });
};
/** A measure typed by hand: digits only, a trailing ",0"/".0" is tolerated. */
var MEASURE_VALUE_RE = /^\d+(\.\d+)?$/;
/**
 * Schema of one product measure (height / width / thickness / weight). The
 * field is a plain text input, so the typed text is trimmed, a decimal comma is
 * turned into a dot and the result is parsed to a number; empty means "not
 * set". The backend stores measures as whole numbers (mm / g), so only
 * non-negative integers pass - anything else gets a field message.
 *
 * @param formatMessage intl message formatter
 * @return optional non-negative integer schema
 */
var createMeasureValueSchema = function (formatMessage) {
    var message = formatMessage({
        id: "ui-br-agency.product.validation.measureValue",
    });
    return z.preprocess(function (val) {
        if (val === null)
            return undefined;
        if (typeof val !== "string")
            return val;
        var normalized = val.trim().replace(",", ".");
        if (normalized === "")
            return undefined;
        return MEASURE_VALUE_RE.test(normalized) ? Number(normalized) : val;
    }, z
        .number({ invalid_type_error: message })
        .int({ message: message })
        .nonnegative({ message: message })
        .optional());
};
export var createProductSchema = function (formatMessage) {
    var requiredOnixItem = function (msgId) {
        return z.preprocess(function (v) { return v !== null && v !== void 0 ? v : {}; }, z
            .object({
            id: z.string().optional(),
            value: z.string().optional(),
        })
            .passthrough()
            .superRefine(function (v, ctx) {
            var hasId = typeof (v === null || v === void 0 ? void 0 : v.id) === "string" && v.id.trim() !== "";
            var hasValue = typeof (v === null || v === void 0 ? void 0 : v.value) === "string" && v.value.trim() !== "";
            if (!hasId && !hasValue) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [],
                    message: formatMessage({ id: msgId }),
                });
            }
        }));
    };
    var hasFormDetailValue = function (row) {
        var value = row === null || row === void 0 ? void 0 : row.value;
        var isFilled = function (v) { return typeof v === "string" && v.trim() !== ""; };
        return isFilled(value === null || value === void 0 ? void 0 : value.id) || isFilled(value === null || value === void 0 ? void 0 : value.value);
    };
    var formDetailSchema = z.object({
        id: z.string().optional(),
        value: requiredOnixItem("ui-br-agency.product.validation.formDetailValue"),
    });
    var extentSchema = z.object({
        id: z.string().optional(),
        type: requiredOnixItem("ui-br-agency.product.validation.extentType"),
        value: z.preprocess(function (val) {
            if (typeof val === "string") {
                var trimmed = val.trim();
                if (trimmed === "")
                    return undefined;
                var num = Number(trimmed);
                return isNaN(num) ? val : num;
            }
            return val;
        }, z.number({
            required_error: formatMessage({
                id: "ui-br-agency.product.validation.extentValue",
            }),
            invalid_type_error: formatMessage({
                id: "ui-br-agency.product.validation.extentValue",
            }),
        })),
        unit: requiredOnixItem("ui-br-agency.product.validation.extentUnit"),
    });
    var measureValue = createMeasureValueSchema(formatMessage);
    var measuresSchema = z
        .object({
        height: measureValue,
        width: measureValue,
        thickness: measureValue,
        weight: measureValue,
    })
        .optional();
    var relationSchema = z.object({
        id: z.string().optional(),
        relationType: requiredOnixItem("ui-br-agency.product.validation.relationType"),
        relatedProductId: z.preprocess(function (v) { return (typeof v === "string" ? v.trim() : ""); }, z.string().min(1, {
            message: formatMessage({
                id: "ui-br-agency.product.validation.relatedProductId",
            }),
        })),
    });
    // Matches the backend rule (Product.deduplicateProductsRelations): at most one
    // relation per related product, whatever its type.
    var relationKey = function (relation) { return relation.relatedProductId || undefined; };
    var priceSchema = z
        .object({
        id: z.string().optional(),
        type: requiredOnixItem("ui-br-agency.product.validation.priceType"),
        amount: z.preprocess(function (val) {
            if (val === "" || val == null)
                return undefined;
            if (typeof val !== "string")
                return val;
            var trimmed = val.trim();
            if (trimmed === "")
                return undefined;
            if (!isValidPriceInput(trimmed))
                return trimmed;
            return priceInputToMinorUnits(trimmed);
        }, z
            .number({
            required_error: formatMessage({
                id: "ui-br-agency.product.validation.priceAmount.required",
            }),
            invalid_type_error: formatMessage({
                id: "ui-br-agency.product.validation.priceAmount.format",
            }),
        })
            .int({
            message: formatMessage({
                id: "ui-br-agency.product.validation.priceAmount.format",
            }),
        })
            .min(0, {
            message: formatMessage({
                id: "ui-br-agency.product.validation.priceAmount.nonNegative",
            }),
        })),
        currency: requiredOnixItem("ui-br-agency.product.validation.priceCurrency"),
    })
        .optional();
    return z
        .object({
        id: z.string().optional(),
        title: z.object({ id: z.string().min(1) }).passthrough(),
        ownerId: z.string().optional(),
        primaryContentType: requiredOnixItem("ui-br-agency.product.validation.primaryContentType"),
        form: requiredOnixItem("ui-br-agency.product.validation.form"),
        // A form detail row without a picked value is optional: it is dropped
        // here (neither validated nor submitted), e.g. the row added
        // automatically after picking an e-book form.
        formDetails: z.preprocess(function (rows) {
            return Array.isArray(rows) ? rows.filter(hasFormDetailValue) : rows;
        }, z.array(formDetailSchema).optional()),
        unitMeasures: measuresSchema,
        cartonMeasures: measuresSchema,
        palletMeasures: measuresSchema,
        extents: z.array(extentSchema).optional(),
        composition: requiredOnixItem("ui-br-agency.product.validation.composition"),
        publishingStatus: requiredOnixItem("ui-br-agency.product.validation.publishingStatus"),
        publishingDate: partialDateSchema(formatMessage),
        copyrightFrom: partialDateSchema(formatMessage),
        copyrightTo: partialDateSchema(formatMessage),
        price: priceSchema,
        // Must be listed here: zod strips keys absent from the schema, which would
        // silently drop the product's related products on every save (issue #74384).
        productsRelations: z
            .array(relationSchema)
            .optional()
            .superRefine(function (relations, ctx) {
            // A related product must not be entered twice (with any relation
            // type): the backend keeps only one relation per related product.
            var seen = new Set();
            relations === null || relations === void 0 ? void 0 : relations.forEach(function (relation, index) {
                var key = relationKey(relation);
                if (!key)
                    return;
                if (seen.has(key)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: [index, "relatedProductId"],
                        message: formatMessage({
                            id: "ui-br-agency.product.validation.relationDuplicate",
                        }),
                    });
                    return;
                }
                seen.add(key);
            });
        }),
        accessibility: z
            .nativeEnum(ProductAccessibilityEnum, {
            errorMap: function () { return ({
                message: formatMessage({
                    id: "ui-br-agency.product.validation.accessibility",
                }),
            }); },
        })
            .optional(),
        publicTime: z.string().optional(),
        version: z.number().optional(),
    })
        .superRefine(function (data, ctx) {
        var from = data.copyrightFrom;
        var to = data.copyrightTo;
        if (from && to) {
            var fromYear = parsePartialDateYear(from);
            var toYear = parsePartialDateYear(to);
            if (fromYear != null && toYear != null && fromYear > toYear) {
                var msg = formatMessage({
                    id: "ui-br-agency.product.validation.copyright.order",
                });
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["copyrightFrom"],
                    message: msg,
                });
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["copyrightTo"],
                    message: msg,
                });
            }
        }
    });
};
export var createLegalPersonSchema = function (formatMessage) {
    return z.object({
        name: z.string().nonempty({
            message: formatMessage({
                id: "ui-br-agency.legal-person.validation.name",
            }),
        }),
        residence: z.string().optional(),
        // Optional for legal persons: sent as MARC 678 without indicators only
        // when filled (DB-495); natural persons require it.
        biographicalNote: z.string().optional(),
        // Local-only authority: kept in ReČeK, not sent to NK authorities (DB-635).
        localOnly: z.boolean().optional(),
        identifiers: z
            .array(z.object({
            id: z.string().optional(),
            type: z.nativeEnum(PersonIdentifierTypeEnum, {
                errorMap: function () { return ({
                    message: formatMessage({
                        id: "ui-br-agency.legal-person.validation.identifierType",
                    }),
                }); },
            }),
            identifier: z.string().nonempty({
                message: formatMessage({
                    id: "ui-br-agency.legal-person.validation.identifier",
                }),
            }),
        }))
            .optional(),
        legalAlternativeNames: z
            .array(z.object({
            id: z.string().optional(),
            name: z.string().nonempty({
                message: formatMessage({
                    id: "ui-br-agency.legal-person.validation.alternativeName",
                }),
            }),
        }))
            .optional(),
    });
};
export var createInlineLegalPersonSchema = function (formatMessage) {
    return createLegalPersonSchema(formatMessage).pick({
        name: true,
        biographicalNote: true,
        localOnly: true,
    });
};
export var createProductNumberSchema = function (formatMessage) {
    return z.object({
        agency: z
            .union([z.literal(""), z.nativeEnum(PublisherRegistrationAgencyEnum)])
            .refine(function (val) { return val !== ""; }, {
            message: formatMessage({
                id: "ui-br-agency.product-number.validation.agency",
            }),
        }),
        value: z
            .string()
            .nonempty({
            message: formatMessage({
                id: "ui-br-agency.product-number.validation.value",
            }),
        })
            .refine(function (val) {
            var normalized = val.replace(/\D/g, "");
            return normalized.length === 13;
        }, {
            message: formatMessage({
                id: "ui-br-agency.product-number.validation.length",
            }),
        }),
        blockReason: z
            .union([z.literal(""), z.nativeEnum(ProductNumberBlockReasonEnum)])
            .refine(function (val) { return val !== ""; }, {
            message: formatMessage({
                id: "ui-br-agency.product-number.validation.blockReason",
            }),
        }),
        blockComment: z.string().optional(),
    });
};
export var createProductNumberAssignSchema = function (formatMessage, isAgencyWorker) {
    var baseSchema = {
        internationalPrefixId: z.string().nonempty({
            message: formatMessage({
                id: "ui-br-agency.product-number.assign.validation.internationalPrefix",
            }),
        }),
        agencyPrefixId: z.string().nonempty({
            message: formatMessage({
                id: "ui-br-agency.product-number.assign.validation.agencyPrefix",
            }),
        }),
        magnitude: z
            .number({
            required_error: formatMessage({
                id: "ui-br-agency.product-number.assign.validation.magnitude",
            }),
        })
            .optional(),
        publisherPrefixId: z.string().optional(),
    };
    if (isAgencyWorker) {
        return z.object(__assign(__assign({}, baseSchema), { publisherId: z.string().nonempty({
                message: formatMessage({
                    id: "ui-br-agency.product-number.assign.validation.publisher",
                }),
            }) }));
    }
    return z.object(baseSchema);
};
export var createProgressReportSchema = function (formatMessage) {
    var optionalDateSchema = z
        .string()
        .optional()
        .refine(function (val) { return !val || moment(val, DISPLAY_DATE_FORMAT, true).isValid(); }, {
        message: formatMessage({
            id: "ui-br-agency.outputs.progress-report.validation.date.format",
        }),
    });
    var optionalEmailSchema = z
        .string()
        .optional()
        .refine(function (val) { return !val || z.string().email().safeParse(val).success; }, {
        message: formatMessage({
            id: "ui-br-agency.outputs.progress-report.validation.email",
        }),
    });
    return z
        .object({
        registrationAgency: z.nativeEnum(ProgressReportParamsRegistrationAgencyEnum),
        periodFrom: optionalDateSchema,
        periodTo: optionalDateSchema,
        chargeForIsbn: z.boolean().optional(),
        pricePerIsbn: z.string().optional(),
        priceFor10: z.string().optional(),
        priceFor100: z.string().optional(),
        priceFor1000: z.string().optional(),
        annualMembership: z.string().optional(),
        registrationFee: z.string().optional(),
        contactPerson: z.string().optional(),
        organization: z.string().optional(),
        department: z.string().optional(),
        street: z.string().optional(),
        city: z.string().optional(),
        country: z.string().optional(),
        telephone1: z.string().optional(),
        telephone2: z.string().optional(),
        telephone3: z.string().optional(),
        fax: z.string().optional(),
        email1: optionalEmailSchema,
        email2: optionalEmailSchema,
        email3: optionalEmailSchema,
        url: z.string().optional(),
        gdprConsent: z.boolean().refine(function (val) { return val === true; }, {
            message: formatMessage({
                id: "ui-br-agency.outputs.progress-report.validation.gdprConsent",
            }),
        }),
        format: z.nativeEnum(FormatEnum),
    })
        .superRefine(function (data, ctx) {
        if (data.periodFrom && data.periodTo) {
            var from = moment(data.periodFrom, DISPLAY_DATE_FORMAT);
            var to = moment(data.periodTo, DISPLAY_DATE_FORMAT);
            if (from.isValid() && to.isValid() && to.isBefore(from)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["periodTo"],
                    message: formatMessage({
                        id: "ui-br-agency.outputs.progress-report.validation.periodTo.afterFrom",
                    }),
                });
            }
        }
    });
};
export var createIdentifierReportSchema = function (formatMessage) {
    return z.object({
        publisherPrefixId: z.string().min(1, {
            message: formatMessage({
                id: "ui-br-agency.outputs.identifier-report.validation.publisherPrefixId.required",
            }),
        }),
    });
};
export var createAnnualReportSchema = function (formatMessage) {
    return z
        .object({
        periodFrom: z.string().min(1, {
            message: formatMessage({
                id: "ui-br-agency.outputs.annual-report.validation.periodFrom.required",
            }),
        }),
        periodTo: z.string().min(1, {
            message: formatMessage({
                id: "ui-br-agency.outputs.annual-report.validation.periodTo.required",
            }),
        }),
    })
        .superRefine(function (data, ctx) {
        if (data.periodFrom && data.periodTo && data.periodFrom > data.periodTo) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["periodTo"],
                message: formatMessage({
                    id: "ui-br-agency.outputs.annual-report.validation.periodTo.afterFrom",
                }),
            });
        }
    });
};
/**
 * Shared validation field for registration agency (ISBN/ISMN).
 * Accepts any RegistrationAgencyEnum since they all share the same values.
 */
var registrationAgencyField = function (formatMessage, enumType) {
    return z.nativeEnum(enumType, {
        errorMap: function () { return ({
            message: formatMessage({
                id: "ui-br-agency.outputs.common.validation.registrationAgency.required",
            }),
        }); },
    });
};
export var createPiidReportSchema = function (formatMessage) {
    return z.object({
        registrationAgency: registrationAgencyField(formatMessage, PiidExportParamsRegistrationAgencyEnum),
    });
};
/** Minimum year accepted by report year validators. */
var MIN_REPORT_YEAR = 1900;
/** Maximum year accepted by report year validators. */
var MAX_REPORT_YEAR = 2100;
var yearSchema = function (formatMessage, translationPrefix) {
    return z.string().refine(function (val) {
        if (!val)
            return false;
        var num = Number(val);
        return (Number.isInteger(num) &&
            num >= MIN_REPORT_YEAR &&
            num <= MAX_REPORT_YEAR);
    }, {
        message: formatMessage({
            id: "".concat(translationPrefix, ".validation.year.invalid"),
        }),
    });
};
export var createScknReportSchema = function (formatMessage) {
    return z.object({
        year: yearSchema(formatMessage, "ui-br-agency.outputs.sckn-report"),
    });
};
export var createKult1501ReportSchema = function (formatMessage) {
    return z.object({
        year: yearSchema(formatMessage, "ui-br-agency.outputs.kult-15-01"),
    });
};
export var createNiposReportSchema = function (formatMessage) {
    return z
        .object({
        periodFrom: z.string().min(1, {
            message: formatMessage({
                id: "ui-br-agency.outputs.nipos-report.validation.periodFrom.required",
            }),
        }),
        periodTo: z.string().min(1, {
            message: formatMessage({
                id: "ui-br-agency.outputs.nipos-report.validation.periodTo.required",
            }),
        }),
        minProductCount: z.string().optional(),
    })
        .superRefine(function (data, ctx) {
        if (data.periodFrom && data.periodTo) {
            var from = toApiDate(data.periodFrom);
            var to = toApiDate(data.periodTo);
            if (from && to && from > to) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["periodTo"],
                    message: formatMessage({
                        id: "ui-br-agency.outputs.nipos-report.validation.periodTo.afterFrom",
                    }),
                });
            }
        }
        if (data.minProductCount) {
            var count = parseInt(data.minProductCount, 10);
            if (isNaN(count) || count < 1) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["minProductCount"],
                    message: formatMessage({
                        id: "ui-br-agency.outputs.nipos-report.validation.minProductCount.invalid",
                    }),
                });
            }
        }
    });
};
export var createPerformanceStatisticsReportSchema = function (formatMessage) {
    return z
        .object({
        periodFrom: z.string().min(1, {
            message: formatMessage({
                id: "ui-br-agency.outputs.performance-statistics.validation.periodFrom.required",
            }),
        }),
        periodTo: z.string().min(1, {
            message: formatMessage({
                id: "ui-br-agency.outputs.performance-statistics.validation.periodTo.required",
            }),
        }),
    })
        .superRefine(function (data, ctx) {
        if (data.periodFrom && data.periodTo) {
            var from = toApiDate(data.periodFrom);
            var to = toApiDate(data.periodTo);
            if (from && to && from > to) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["periodTo"],
                    message: formatMessage({
                        id: "ui-br-agency.outputs.performance-statistics.validation.periodTo.afterFrom",
                    }),
                });
            }
        }
    });
};
export var createWipoReportSchema = function (formatMessage) {
    return z.object({
        year: z
            .string()
            .nonempty({
            message: formatMessage({
                id: "ui-br-agency.outputs.wipo-report.validation.year.required",
            }),
        })
            .regex(/^\d{4}$/, {
            message: formatMessage({
                id: "ui-br-agency.outputs.wipo-report.validation.year.format",
            }),
        }),
        registrationAgency: registrationAgencyField(formatMessage, WipoReportParamsRegistrationAgencyEnum),
    });
};
export var createIpaReportSchema = function (formatMessage) {
    return z.object({
        year: z
            .string()
            .nonempty({
            message: formatMessage({
                id: "ui-br-agency.outputs.ipa-report.validation.year.required",
            }),
        })
            .regex(/^\d{4}$/, {
            message: formatMessage({
                id: "ui-br-agency.outputs.ipa-report.validation.year.format",
            }),
        }),
        registrationAgency: z.nativeEnum(IpaReportParamsRegistrationAgencyEnum, {
            errorMap: function () { return ({
                message: formatMessage({
                    id: "ui-br-agency.outputs.common.validation.registrationAgency.required",
                }),
            }); },
        }),
    });
};
export var createOnixExportSchema = function (formatMessage) {
    return z
        .object({
        modifiedSince: z.string().optional(),
        modifiedBefore: z.string().optional(),
        isbns: z.string().optional(),
        titleIds: z.string().optional(),
        offset: z.string().refine(function (val) {
            var num = parseInt(val, 10);
            return !isNaN(num) && num >= 0;
        }, {
            message: formatMessage({
                id: "ui-br-agency.outputs.onix-export.validation.offset",
            }),
        }),
        limit: z.string(),
    })
        .superRefine(function (data, ctx) {
        if (data.modifiedSince && data.modifiedBefore) {
            var from = toApiDate(data.modifiedSince);
            var to = toApiDate(data.modifiedBefore);
            if (from && to && from > to) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["modifiedBefore"],
                    message: formatMessage({
                        id: "ui-br-agency.outputs.onix-export.validation.modifiedBefore.afterSince",
                    }),
                });
            }
        }
    });
};
export var createDeclareLastUsedNumberSchema = function (formatMessage) {
    return z
        .object({
        lastUsedNumber: z.string().optional(),
        noNumberUsed: z.boolean(),
    })
        .refine(function (data) { var _a; return data.noNumberUsed || ((_a = data.lastUsedNumber) !== null && _a !== void 0 ? _a : "").trim().length > 0; }, {
        message: formatMessage({
            id: "ui-br-agency.publisher-prefix.declare.validation.required",
        }),
        path: ["lastUsedNumber"],
    })
        .refine(function (data) {
        var _a, _b;
        return data.noNumberUsed ||
            ((_a = data.lastUsedNumber) !== null && _a !== void 0 ? _a : "").trim().length === 0 ||
            validateEAN13(((_b = data.lastUsedNumber) !== null && _b !== void 0 ? _b : "").replace(/\D/g, ""));
    }, {
        message: formatMessage({
            id: "ui-br-agency.publisher-prefix.declare.error.-53",
        }),
        path: ["lastUsedNumber"],
    });
};
